import { z } from 'zod';
/**
 * Map Types - Unreal-Friendly Battle Map Data Model
 *
 * Design Philosophy:
 * - Web canvas (Pixi.js) is the "preview mode" for GM prep
 * - Data model designed for future Unreal Engine integration
 * - Units in world space (feet/meters), not pixels
 * - Support for future PBR materials, height maps, 3D terrain
 */
// =============================================================================
// WORLD UNITS
// =============================================================================
/**
 * Position in world units (feet by default, matching D&D 5e)
 * Not pixels - renderer converts to screen space
 */
export const WorldPositionSchema = z.object({
    x: z.number(), // feet
    y: z.number(), // feet
    z: z.number().default(0), // elevation in feet (for flying, multi-level)
});
/**
 * World bounds in feet
 */
export const WorldBoundsSchema = z.object({
    width: z.number(), // feet
    height: z.number(), // feet
    originX: z.number().default(0), // world offset
    originY: z.number().default(0),
});
// =============================================================================
// MAP ENVIRONMENT
// =============================================================================
/**
 * Environment type affects AI generation prompts and Unreal material selection
 */
export const MapEnvironmentSchema = z.enum([
    // Dungeons
    'dungeon_stone',
    'dungeon_crypt',
    'dungeon_sewer',
    'dungeon_mine',
    'dungeon_temple',
    'dungeon_prison',
    // Natural
    'cave_natural',
    'cave_crystal',
    'cave_lava',
    'cave_ice',
    // Outdoor
    'forest_clearing',
    'forest_dense',
    'swamp',
    'desert',
    'mountain',
    'beach',
    'grassland',
    'snow',
    // Urban
    'tavern_interior',
    'castle_interior',
    'castle_courtyard',
    'street_medieval',
    'street_slum',
    'marketplace',
    'docks',
    'ship_deck',
    // Jungle / Tropical
    'jungle_river',
    'jungle_ruins',
    'jungle_clearing',
    // Special
    'arcane_library',
    'throne_room',
    'arena',
    'racetrack',
    'graveyard',
    'bridge',
    'custom',
]);
/**
 * Visual style for AI generation
 */
export const MapStyleSchema = z.enum([
    'top_down_tactical', // Classic VTT style
    'top_down_painted', // More artistic/painted look
    'isometric', // 2.5D isometric view
    'perspective', // Slight perspective (for 3D feel)
]);
/**
 * Lighting mood presets (affects generation + Unreal lighting)
 */
export const MapMoodSchema = z.enum([
    'bright_day',
    'overcast',
    'golden_hour',
    'twilight',
    'moonlit',
    'torchlit',
    'dark_ambient',
    'magical_glow',
    'ominous',
    'cozy',
]);
// =============================================================================
// MATERIAL ZONES (for Unreal PBR)
// =============================================================================
/**
 * Material type hints for Unreal Engine PBR materials
 * Even in 2D, we track what surfaces are what for future 3D rendering
 */
export const MaterialTypeSchema = z.enum([
    'stone_floor',
    'stone_wall',
    'wood_floor',
    'wood_planks',
    'dirt',
    'grass',
    'water_shallow',
    'water_deep',
    'lava',
    'ice',
    'sand',
    'carpet',
    'tile',
    'metal_grate',
    'void', // Pits, chasms
]);
/**
 * A zone on the map with specific material properties
 * Used for Unreal material assignment and gameplay (difficult terrain, etc.)
 */
export const MaterialZoneSchema = z.object({
    id: z.string().uuid(),
    materialType: MaterialTypeSchema,
    // Polygon vertices in world coordinates (feet)
    vertices: z.array(WorldPositionSchema).min(3),
    // Gameplay properties
    isDifficultTerrain: z.boolean().default(false),
    isHazard: z.boolean().default(false),
    hazardDamage: z.string().optional(), // e.g., "1d6 fire"
    isWater: z.boolean().default(false),
    waterDepth: z.number().optional(), // feet
    // Visual overrides
    tintColor: z.string().optional(), // hex color
    opacity: z.number().min(0).max(1).default(1),
});
// =============================================================================
// MAP TEXTURES
// =============================================================================
/**
 * Texture layer with optional depth/normal data
 * Core data for both 2D preview and 3D Unreal rendering
 */
export const MapTextureSchema = z.object({
    id: z.string().uuid(),
    // Primary color texture (always present)
    colorUrl: z.string().url(),
    // Optional PBR textures for Unreal (generated later or from AI)
    depthUrl: z.string().url().optional(), // Height/depth map
    normalUrl: z.string().url().optional(), // Normal map for lighting
    roughnessUrl: z.string().url().optional(), // PBR roughness
    aoUrl: z.string().url().optional(), // Ambient occlusion
    // Texture bounds in world space
    bounds: WorldBoundsSchema,
    // Resolution info
    pixelWidth: z.number().int().positive(),
    pixelHeight: z.number().int().positive(),
    pixelsPerFoot: z.number().positive().default(10), // Resolution scaling
    // Layer ordering
    zIndex: z.number().int().default(0),
    opacity: z.number().min(0).max(1).default(1),
});
// =============================================================================
// AI GENERATION
// =============================================================================
/**
 * Prompt template for AI map generation
 */
export const MapGenerationPromptSchema = z.object({
    // User input
    userPrompt: z.string(), // Free-form description
    // Structured parameters
    environment: MapEnvironmentSchema,
    style: MapStyleSchema,
    mood: MapMoodSchema,
    // Size
    widthFeet: z.number().positive().default(100),
    heightFeet: z.number().positive().default(100),
    // Optional features to include
    features: z.array(z.string()).default([]), // e.g., ["central pillar", "pit trap", "altar"]
    // Negative prompts (things to avoid)
    avoid: z.array(z.string()).default([]),
});
/**
 * Result from AI generation
 */
export const MapGenerationResultSchema = z.object({
    id: z.string().uuid(),
    // Input that generated this
    prompt: MapGenerationPromptSchema,
    // Generated assets
    texture: MapTextureSchema,
    // Generation metadata
    model: z.string(), // e.g., "dall-e-3", "flux-1.1"
    generatedAt: z.coerce.date(),
    generationTimeMs: z.number().int().positive(),
    cost: z.number().optional(), // USD if tracked
    // Quality/rating (for future ML training)
    rating: z.number().min(1).max(5).optional(),
    tags: z.array(z.string()).default([]),
});
// =============================================================================
// BATTLE MAP (Complete Map Definition)
// =============================================================================
/**
 * Complete battle map with all layers and metadata
 * This is the Unreal-ready data structure
 */
export const BattleMapSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    // World dimensions
    bounds: WorldBoundsSchema,
    // Grid configuration
    gridType: z.enum(['square', 'hex', 'none']).default('square'),
    gridSizeFeet: z.number().positive().default(5), // 5ft squares standard D&D
    // Background/base texture
    baseTexture: MapTextureSchema.optional(),
    // Additional texture layers (overlays, decorations)
    textureLayers: z.array(MapTextureSchema).default([]),
    // Material zones for gameplay and Unreal materials
    materialZones: z.array(MaterialZoneSchema).default([]),
    // Environment metadata
    environment: MapEnvironmentSchema.default('dungeon_stone'),
    mood: MapMoodSchema.default('torchlit'),
    // Generation style — determines camera angle and whether iso warp is needed
    style: MapStyleSchema.default('top_down_tactical'),
    // Generation history (if AI-generated)
    generationHistory: z.array(MapGenerationResultSchema).default([]),
    // Walls/obstacles for line of sight (simple representation)
    // Future: full 3D geometry for Unreal
    walls: z
        .array(z.object({
        id: z.string().uuid(),
        start: WorldPositionSchema,
        end: WorldPositionSchema,
        height: z.number().default(10), // feet
        blocksVision: z.boolean().default(true),
        blocksMovement: z.boolean().default(true),
        material: MaterialTypeSchema.optional(),
    }))
        .default([]),
    // Doors (interactive wall segments)
    doors: z
        .array(z.object({
        id: z.string().uuid(),
        position: WorldPositionSchema,
        width: z.number().default(5), // feet
        isOpen: z.boolean().default(false),
        isLocked: z.boolean().default(false),
        isSecret: z.boolean().default(false),
    }))
        .default([]),
    // Campaign association
    campaignId: z.string().uuid().optional(),
    sceneId: z.string().uuid().optional(),
    // Metadata
    tags: z.array(z.string()).default([]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    createdBy: z.string().optional(), // User ID
});
// =============================================================================
// HELPERS
// =============================================================================
/**
 * Convert world feet to pixels for 2D rendering
 */
export function feetToPixels(feet, pixelsPerFoot = 10) {
    return feet * pixelsPerFoot;
}
/**
 * Convert pixels to world feet
 */
export function pixelsToFeet(pixels, pixelsPerFoot = 10) {
    return pixels / pixelsPerFoot;
}
/**
 * Get default prompt for an environment
 */
export function getEnvironmentPrompt(env) {
    const prompts = {
        dungeon_stone: 'stone dungeon corridor with worn flagstones and ancient pillars',
        dungeon_crypt: 'dark crypt with sarcophagi, cobwebs, and skeletal remains',
        dungeon_sewer: 'damp sewer tunnel with water channels and grime-covered walls',
        dungeon_mine: 'abandoned mine shaft with wooden supports and ore veins',
        dungeon_temple: 'ancient temple interior with religious iconography and altar',
        dungeon_prison: 'prison cells with iron bars and stone walls',
        cave_natural: 'natural cave with stalactites and uneven rocky floor',
        cave_crystal: 'crystalline cave with glowing gems embedded in walls',
        cave_lava: 'volcanic cave with lava flows and obsidian formations',
        cave_ice: 'frozen ice cave with icicles and slippery surfaces',
        forest_clearing: 'forest clearing surrounded by tall trees',
        forest_dense: 'dense forest floor with thick undergrowth',
        swamp: 'murky swamp with dead trees and stagnant water',
        desert: 'desert sand dunes with scattered rocks',
        mountain: 'rocky mountain path with steep cliffs',
        beach: 'sandy beach with waves and scattered driftwood',
        grassland: 'open grassland with rolling hills',
        snow: 'snow-covered landscape with frost and ice',
        jungle_river: 'tropical river cutting through dense jungle canopy, murky water, overhanging vines and ferns',
        jungle_ruins: 'overgrown stone ruins in tropical jungle, crumbling walls, vine-covered pillars',
        jungle_clearing: 'jungle clearing with trampled undergrowth, thick canopy border, animal trails',
        tavern_interior: 'cozy tavern interior with wooden tables and fireplace',
        castle_interior: 'castle great hall with stone columns and tapestries',
        castle_courtyard: 'castle courtyard with cobblestones and walls',
        street_medieval: 'medieval town street with timber buildings',
        street_slum: 'cramped slum alley with ramshackle buildings',
        marketplace: 'busy marketplace with stalls and crates',
        docks: 'wooden dock with moored boats and cargo',
        ship_deck: 'sailing ship deck with masts and rigging',
        arcane_library: 'magical library with floating books and arcane circles',
        throne_room: 'royal throne room with ornate decorations',
        arena: 'gladiatorial arena with sand floor and spectator stands',
        racetrack: 'dinosaur racing track in a tropical jungle city, wooden grandstands, sandy oval track with painted lanes, colorful banners and pennants, excited crowd, Port Nyanzaru style',
        graveyard: 'spooky graveyard with tombstones and dead trees',
        bridge: 'stone bridge over a chasm or river',
        custom: '',
    };
    return prompts[env];
}
/**
 * Build full generation prompt from structured input
 */
export function buildGenerationPrompt(input) {
    const parts = [];
    // Style prefix
    const stylePrefix = {
        top_down_tactical: 'top-down tactical battle map, clean grid-friendly layout, fantasy RPG style',
        top_down_painted: 'top-down painted battle map, artistic hand-drawn style, rich colors',
        isometric: 'isometric battle map, 2.5D HD-2D perspective looking down at 30 degree angle, detailed environment, Octopath Traveler ground plane style, rich textures and depth',
        perspective: 'slight perspective battle map, dynamic angle, immersive view',
    };
    parts.push(stylePrefix[input.style]);
    // Environment
    const envPrompt = getEnvironmentPrompt(input.environment);
    if (envPrompt) {
        parts.push(envPrompt);
    }
    // User description
    if (input.userPrompt) {
        parts.push(input.userPrompt);
    }
    // Mood
    const moodSuffix = {
        bright_day: 'bright daylight, clear visibility',
        overcast: 'overcast sky, diffuse lighting',
        golden_hour: 'golden hour warm lighting, long shadows',
        twilight: 'twilight purple-blue atmosphere',
        moonlit: 'moonlit night, silver highlights, deep shadows',
        torchlit: 'torchlit, warm orange flickering light, deep shadows',
        dark_ambient: 'dark ambient, minimal light sources',
        magical_glow: 'magical glowing light, ethereal atmosphere',
        ominous: 'ominous atmosphere, foreboding shadows',
        cozy: 'cozy warm atmosphere, inviting',
    };
    parts.push(moodSuffix[input.mood]);
    // Features
    if (input.features.length > 0) {
        parts.push(`featuring: ${input.features.join(', ')}`);
    }
    // Build final prompt
    let prompt = parts.join(', ');
    // Add negative prompts as suffix
    if (input.avoid.length > 0) {
        prompt += `. Avoid: ${input.avoid.join(', ')}`;
    }
    return prompt;
}
//# sourceMappingURL=map-types.js.map